import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Brand, Logo } from "@/components/Logo";
import { ShieldCheck, Check, Loader2, Lock, LockOpen, Download, Printer, BadgeCheck } from "lucide-react";
import { meta } from "@/analytics/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SbloccoPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(!!sessionId);
  const purchaseSent = useRef(false);

  const load = useCallback(() => {
    fetch(`${API}/sblocco/${orderId}?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Link non valido");
        return r.json();
      })
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((e) => setState({ loading: false, error: e.message, data: null }));
  }, [orderId, token]);

  useEffect(() => {
    let cancelled = false;
    const confirmPayment = async () => {
      if (!sessionId) return load();
      for (let i = 0; i < 24; i++) {
        try {
          const r = await fetch(`${API}/payments/status/${sessionId}`);
          const d = await r.json();
          if (d.payment_status === "paid") {
            if (!purchaseSent.current && d.amount) {
              purchaseSent.current = true;
              meta.purchase({
                value: d.amount,
                currency: (d.currency || "eur").toUpperCase(),
                content_ids: d.lookup_key ? [d.lookup_key] : [],
                content_type: "product",
                num_items: 1,
              });
            }
            break;
          }
        } catch { /* retry */ }
        await new Promise((res) => setTimeout(res, 2500));
        if (cancelled) return;
      }
      if (!cancelled) { setVerifying(false); load(); }
    };
    confirmPayment();
    return () => { cancelled = true; };
  }, [sessionId, load]);

  const paga = async () => {
    setPaying(true);
    if (state.data?.price) {
      meta.initiateCheckout({ value: state.data.price, currency: "EUR", content_type: "product", num_items: 1 });
    }
    try {
      const r = await fetch(`${API}/sblocco/${orderId}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ t: token, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Errore pagamento");
      window.location.href = d.checkout_url;
    } catch (err) {
      alert(err.message);
      setPaying(false);
    }
  };

  const d = state.data;
  const priceTxt = d?.price ? `${d.price.toLocaleString("it-IT")}€` : "";

  return (
    <div className="min-h-screen bg-[#F6FAFD]" data-testid="sblocco-page">
      <header className="bg-white border-b border-[#E2E8F0] py-5 px-5">
        <div className="max-w-3xl mx-auto"><Brand /></div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        {(state.loading || verifying) && (
          <div className="flex flex-col items-center py-20 gap-3" data-testid="sblocco-loading">
            <Loader2 className="animate-spin text-[#00AEEF]" size={32} />
            {verifying && <p className="text-sm text-[#64748B]">Conferma del pagamento in corso…</p>}
          </div>
        )}

        {state.error && (
          <div className="card rounded-3xl p-10 text-center" data-testid="sblocco-error">
            <h1 className="text-xl font-bold text-[#003087]">Esito non disponibile</h1>
            <p className="mt-3 text-sm text-[#64748B]">{state.error}. Se hai ricevuto il link via email, verifica di averlo aperto per intero o contattaci su WhatsApp.</p>
          </div>
        )}

        {d && !verifying && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">Esito pratica</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#003087] mt-3" data-testid="sblocco-title">
                {d.name}, la tua cancellazione è avvenuta.
              </h1>
              <p className="mt-3 text-sm text-[#64748B]">
                {d.segnalazioni != null && <>Segnalazioni lavorate: <strong className="text-[#003087]">{d.segnalazioni}</strong>. </>}
                Pacchetto concordato: <strong className="text-[#003087]">{d.label} — {priceTxt}</strong>
              </p>
            </div>

            {!d.pagato && (
              <div className="rounded-3xl bg-[#003087] text-white p-8 shadow-2xl relative overflow-hidden" data-testid="sblocco-locked">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#00AEEF]/20 blur-[80px] pointer-events-none" />
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] bg-white/10 rounded-full px-4 py-1.5 w-fit">
                  <Lock size={12} /> Certificato e visura pulita bloccati
                </div>
                <p className="mt-5 text-sm text-[#DCE7F5] leading-relaxed">
                  Come da contratto, il pacchetto si paga solo a risultato ottenuto — e il risultato è arrivato.
                  Al pagamento si sbloccano subito il certificato di avvenuta cancellazione e la tua nuova visura pulita.
                </p>
                <div className="mt-6 flex items-end gap-3">
                  <span className="text-6xl font-extrabold tracking-tight" data-testid="sblocco-price">{priceTxt}</span>
                  <span className="text-[#9BDDF7] text-xs font-bold mb-2.5 uppercase tracking-wide">anche in 3 o 6 rate senza interessi</span>
                </div>
                <button data-testid="sblocco-pay-btn" onClick={paga} disabled={paying}
                  className="btn-green mt-8 w-full rounded-full py-4 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {paying ? <Loader2 size={15} className="animate-spin" /> : <LockOpen size={15} />} Paga e sblocca il certificato
                </button>
                <p className="mt-3 text-center text-[11px] text-[#8FA8CC] flex items-center justify-center gap-1.5">
                  <ShieldCheck size={12} /> Pagamento sicuro Stripe · Carte · Klarna
                </p>
              </div>
            )}

            {d.pagato && (
              <div className="space-y-6">
                <div className="rounded-3xl bg-white border border-[#E2E8F0] p-8 shadow-sm" data-testid="sblocco-unlocked">
                  <div className="flex items-center gap-2 text-[#00AEEF] text-xs font-bold uppercase tracking-wider">
                    <BadgeCheck size={16} /> Pagamento ricevuto — documenti sbloccati
                  </div>

                  <div className="mt-6 border-2 border-[#003087] rounded-2xl p-8 text-center print:border-4" data-testid="certificate">
                    <div className="flex justify-center"><Logo size={44} /></div>
                    <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#00AEEF] font-bold">Cancellazione Crif Italia · Famiglia Sdebitiamoci</p>
                    <h2 className="mt-3 text-xl font-extrabold text-[#003087]">Attestato di avvenuta cancellazione</h2>
                    <p className="mt-4 text-sm text-[#1A1A1A] leading-relaxed max-w-md mx-auto">
                      Si attesta che la pratica di <strong>{d.name}</strong> è stata lavorata con esito positivo:
                      le segnalazioni oggetto di incarico risultano cancellate dalle banche dati creditizie interessate.
                    </p>
                    <p className="mt-4 text-xs text-[#64748B]">Pacchetto: {d.label} · Pratica n. {orderId.slice(0, 8).toUpperCase()} · Data: {new Date().toLocaleDateString("it-IT")}</p>
                    <p className="mt-2 text-[10px] text-[#94A3B8]">La prova ufficiale è costituita dalla visura aggiornata rilasciata dall'ente creditizio.</p>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <button data-testid="sblocco-print-btn" onClick={() => window.print()}
                      className="btn-outline rounded-full py-3.5 text-sm font-bold flex items-center justify-center gap-2">
                      <Printer size={15} /> Stampa l'attestato
                    </button>
                    {d.has_visura ? (
                      <a data-testid="sblocco-visura-btn" href={`${API}/sblocco/${orderId}/visura?t=${encodeURIComponent(token)}`}
                        className="btn-green rounded-full py-3.5 text-sm font-bold flex items-center justify-center gap-2">
                        <Download size={15} /> Scarica la visura pulita
                      </a>
                    ) : (
                      <p className="text-xs text-[#94A3B8] flex items-center justify-center text-center px-3" data-testid="sblocco-visura-pending">
                        La visura pulita sarà caricata a breve dal nostro team: ti avvisiamo via email.
                      </p>
                    )}
                  </div>
                </div>

                <ul className="space-y-2.5">
                  {["Conserva attestato e visura: ti serviranno per mutui e finanziamenti", "Il tuo merito creditizio torna valutabile dalle banche", "Per qualsiasi dubbio scrivici su WhatsApp"].map((f, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-[#1A1A1A]"><Check size={16} className="text-[#00AEEF] shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
